/**
 * Freight panel on a quote, for Kono operators (kono.ai accounts) only.
 *
 * Per line: the pickup address (the vendor's ship-from, correctable), the
 * delivery address (the customer's, correctable), the carrier options for
 * that leg (WWEX LTL, UPS parcel through WWEX) with a "Get the cost" button
 * for a live rate, and the decision: add the cost to the quote (the customer
 * pays it, the quote total changes), paid by Kono (recorded, nothing added
 * for the customer), or skip for now. Saving records the plan on the quote;
 * the order made from it carries the plan so ops see what Kono pays.
 */
import { useEffect, useMemo, useState } from "react";
import {
  FreightAddress,
  FreightDecision,
  FreightOption,
  FreightPanelView,
  FreightPickup,
  FreightPlanLine,
  FreightReceiving,
  Quote,
  loadFreightPanel,
  messageOf,
  rateFreightLine,
  saveFreightPlan,
} from "./api";

const INPUT = "rounded border border-gray-300 px-2 py-1 text-sm";
const BUTTON = "rounded-full bg-[#00062e] px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-50";
const BUTTON_LIGHT = "rounded-full border border-gray-300 px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-50";

const money = (amount: number, currency: string) =>
  `${currency === "USD" ? "$" : `${currency} `}${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const DECISIONS: { value: FreightDecision; label: string; hint: string }[] = [
  { value: "add_to_quote", label: "Add to the quote", hint: "The customer pays it: the quote total goes up by this amount." },
  { value: "kono_pays", label: "Paid by Kono", hint: "Kono absorbs it: recorded on the quote and the order, nothing added for the customer." },
  { value: "skip", label: "Skip for now", hint: "Decide later; nothing changes." },
];

type AddressDraft = { street: string; city: string; state: string; postalCode: string; countryCode: string };

const draftOf = (a: FreightAddress | null): AddressDraft => ({
  street: (a?.streetLines || []).join(", "),
  city: a?.city || "",
  state: a?.stateOrProvinceCode || "",
  postalCode: a?.postalCode || "",
  countryCode: a?.countryCode || "US",
});

const addressOf = (d: AddressDraft): FreightAddress | null => {
  const streetLines = d.street.split(/\s*,\s*/).map((s) => s.trim()).filter(Boolean);
  if (!d.city.trim() && !d.postalCode.trim() && streetLines.length === 0) return null;
  return {
    streetLines,
    city: d.city.trim(),
    postalCode: d.postalCode.trim(),
    countryCode: (d.countryCode.trim() || "US").toUpperCase(),
    ...(d.state.trim() ? { stateOrProvinceCode: d.state.trim().toUpperCase() } : {}),
  };
};

const oneLine = (a: FreightAddress | null): string =>
  a ? [a.streetLines.join(", "), a.city, a.stateOrProvinceCode, a.postalCode, a.countryCode !== "US" ? a.countryCode : ""].filter(Boolean).join(", ") : "none";

type LineState = FreightPlanLine & { originDraft: AddressDraft; destinationDraft: AddressDraft; editingOrigin: boolean; editingDestination: boolean; rating: boolean };

const AddressBlock = ({
  title,
  source,
  line,
  which,
  onChange,
  onToggle,
}: {
  title: string;
  source: string;
  line: LineState;
  which: "origin" | "destination";
  onChange: (patch: Partial<AddressDraft>) => void;
  onToggle: () => void;
}) => {
  const editing = which === "origin" ? line.editingOrigin : line.editingDestination;
  const draft = which === "origin" ? line.originDraft : line.destinationDraft;
  const address = which === "origin" ? line.origin : line.destination;
  return (
    <div className="rounded border border-gray-200 bg-gray-50 p-2 text-xs">
      <div className="flex items-center justify-between">
        <span className="font-medium text-[#00062e]">
          {title} <span className="font-normal text-gray-500">({source})</span>
        </span>
        <button type="button" className="text-blue-700 underline" onClick={onToggle}>
          {editing ? "Done" : "Correct"}
        </button>
      </div>
      {editing ? (
        <div className="mt-1 grid grid-cols-2 gap-1">
          <input className={`${INPUT} col-span-2`} placeholder="Street (commas between lines)" value={draft.street} onChange={(e) => onChange({ street: e.target.value })} />
          <input className={INPUT} placeholder="City" value={draft.city} onChange={(e) => onChange({ city: e.target.value })} />
          <input className={INPUT} placeholder="State" value={draft.state} onChange={(e) => onChange({ state: e.target.value })} />
          <input className={INPUT} placeholder="ZIP" value={draft.postalCode} onChange={(e) => onChange({ postalCode: e.target.value })} />
          <input className={INPUT} placeholder="Country" value={draft.countryCode} maxLength={2} onChange={(e) => onChange({ countryCode: e.target.value })} />
        </div>
      ) : (
        <div className={`mt-1 ${address ? "text-gray-800" : "text-red-700"}`}>{oneLine(address)}</div>
      )}
    </div>
  );
};

/**
 * The pickup side of a line, for the carrier: the vendor company the driver
 * confirms on arrival, the person at the facility who knows about the
 * shipment, the day the material is ready, the window during the day the
 * dock hands it over, and anything else the driver needs.
 */
const PickupBlock = ({ pickup, editable, onChange }: { pickup: FreightPickup; editable: boolean; onChange: (patch: Partial<FreightPickup>) => void }) => {
  const missing = [!pickup.company && "company", !pickup.name && "contact name", !pickup.phone && "phone", !(pickup.readyTime && pickup.closeTime) && "pickup window"].filter(Boolean) as string[];
  return (
    <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-xs">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-medium text-[#00062e]">
          Pickup at the vendor <span className="font-normal text-gray-500">(what the carrier gets: who to ask for, when the dock hands it over)</span>
        </span>
        {missing.length ? <span className="text-amber-900">Missing: {missing.join(", ")}</span> : <span className="text-green-800">Complete</span>}
      </div>
      <div className="mt-1 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <label className="text-gray-600">
          Vendor company (the driver confirms it on arrival)
          <input className={`${INPUT} w-full`} value={pickup.company} disabled={!editable} onChange={(e) => onChange({ company: e.target.value })} />
        </label>
        <label className="text-gray-600">
          Contact at the facility
          <input className={`${INPUT} w-full`} value={pickup.name} disabled={!editable} placeholder="who knows about this shipment" onChange={(e) => onChange({ name: e.target.value })} />
        </label>
        <label className="text-gray-600">
          Phone (the driver calls this number)
          <input className={`${INPUT} w-full`} value={pickup.phone} disabled={!editable} onChange={(e) => onChange({ phone: e.target.value })} />
        </label>
        <label className="text-gray-600">
          Email (gets the BOL and status emails)
          <input className={`${INPUT} w-full`} value={pickup.email} disabled={!editable} onChange={(e) => onChange({ email: e.target.value })} />
        </label>
        <label className="text-gray-600">
          Ready for pickup on or after
          <input type="date" className={`${INPUT} w-full`} value={pickup.readyDate} disabled={!editable} onChange={(e) => onChange({ readyDate: e.target.value })} />
        </label>
        <label className="text-gray-600">
          Pickup window from
          <input type="time" className={`${INPUT} w-full`} value={pickup.readyTime} disabled={!editable} onChange={(e) => onChange({ readyTime: e.target.value })} />
        </label>
        <label className="text-gray-600">
          Pickup window until (dock closes)
          <input type="time" className={`${INPUT} w-full`} value={pickup.closeTime} disabled={!editable} onChange={(e) => onChange({ closeTime: e.target.value })} />
        </label>
        <label className="text-gray-600 sm:col-span-2 lg:col-span-4">
          Pickup instructions for the driver (WWEX prints the first 60 characters on the BOL)
          <input className={`${INPUT} w-full`} value={pickup.instructions} disabled={!editable} placeholder="e.g. dock 3, ask for the shipping desk, forklift on site, bring straps" onChange={(e) => onChange({ instructions: e.target.value })} />
        </label>
      </div>
    </div>
  );
};

export const OperatorFreightPanel = ({ quote, onSaved }: { quote: Quote; onSaved: (quote: Quote, revised: boolean, summary: string) => void }) => {
  const [view, setView] = useState<FreightPanelView | null>(null);
  const [lines, setLines] = useState<LineState[]>([]);
  const [note, setNote] = useState("");
  const [receiving, setReceiving] = useState<FreightReceiving>({ company: "", name: "", phone: "", email: "" });
  const [deliveryInstructions, setDeliveryInstructions] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setView(null);
    setError("");
    loadFreightPanel(quote.id)
      .then((v) => {
        if (cancelled) return;
        setView(v);
        setNote(v.plan.note || "");
        setReceiving(v.plan.receiving || { company: v.customer?.company || "", name: v.customer?.name || "", phone: v.customer?.phone || "", email: v.customer?.email || "" });
        setDeliveryInstructions(v.plan.deliveryInstructions || "");
        setLines(
          v.plan.lines.map((l) => ({
            ...l,
            originDraft: draftOf(l.origin),
            destinationDraft: draftOf(l.destination),
            editingOrigin: false,
            editingDestination: false,
            rating: false,
          })),
        );
      })
      .catch((e) => !cancelled && setError(messageOf(e)));
    return () => {
      cancelled = true;
    };
  }, [quote.id]);

  const patch = (index: number, p: Partial<LineState> | ((l: LineState) => Partial<LineState>)) =>
    setLines((all) => all.map((l) => (l.index === index ? { ...l, ...(typeof p === "function" ? p(l) : p) } : l)));

  const applyAddress = (line: LineState, which: "origin" | "destination"): Partial<LineState> => {
    const next = addressOf(which === "origin" ? line.originDraft : line.destinationDraft);
    const changed = JSON.stringify(next) !== JSON.stringify(which === "origin" ? line.origin : line.destination);
    return {
      [which]: next,
      ...(changed ? { options: [], selected: null, decision: "skip" as FreightDecision, quotedAt: null, quoteError: null } : {}),
      ...(which === "origin" ? { editingOrigin: false, ...(changed ? { originSource: "operator" as const } : {}) } : { editingDestination: false, ...(changed ? { destinationSource: "operator" as const } : {}) }),
    };
  };

  const rate = async (line: LineState) => {
    patch(line.index, { rating: true, quoteError: null });
    try {
      const result = await rateFreightLine(quote.id, line.index, line.origin, line.destination);
      patch(line.index, (l) => ({
        rating: false,
        options: result.options,
        quotedAt: result.quotedAt,
        quoteError: result.error,
        selected: result.options.find((o) => l.selected && o.vendorId === l.selected.vendorId && o.serviceCode === l.selected.serviceCode) || result.options[0] || null,
      }));
    } catch (e) {
      patch(line.index, { rating: false, quoteError: messageOf(e) });
    }
  };

  const totals = useMemo(() => {
    let customer = 0;
    let kono = 0;
    for (const l of lines) {
      if (!l.selected) continue;
      if (l.decision === "add_to_quote") customer += l.selected.amount;
      if (l.decision === "kono_pays") kono += l.selected.amount;
    }
    return { customer: Math.round(customer * 100) / 100, kono: Math.round(kono * 100) / 100 };
  }, [lines]);

  const currency = view?.currency || "USD";
  const undecided = lines.filter((l) => l.decision !== "skip" && !l.selected);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      if (undecided.length) throw new Error(`Line ${undecided.map((l) => l.index + 1).join(", ")}: get the cost and pick a carrier option before deciding who pays it.`);
      const result = await saveFreightPlan(quote.id, {
        lines: lines.map((l) => ({
          index: l.index,
          origin: l.origin,
          destination: l.destination,
          options: l.options,
          selected: l.selected,
          decision: l.decision,
          quotedAt: l.quotedAt,
          quoteError: l.quoteError,
          pickup: l.pickup,
        })),
        note,
        receiving,
        deliveryInstructions,
      });
      const decided = result.plan.lines.filter((l) => l.decision !== "skip" && l.selected);
      const summary = decided.length
        ? `freight: customer pays ${money(result.plan.customerCharge, result.plan.currency)}, Kono pays ${money(result.plan.konoPays, result.plan.currency)}`
        : "freight: nothing decided yet (addresses saved)";
      onSaved(result.quote, result.revised, summary);
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div id="operator-freight-panel" className="mt-4 rounded-xl border border-sky-300 bg-sky-50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-[#00062e]">Freight, internal: pickup and delivery details for the carrier, cost and who pays, per line</h2>
      </div>
      <p className="mt-1 text-xs text-gray-700">
        For each line: confirm where it ships from (the vendor's dock) and to (the customer), correct either if needed, fill in what the carrier needs at the vendor's dock (company name, the person who knows
        about the shipment, the ready day and the pickup window), get the WWEX cost for the leg, then decide: add it to the quote (the customer pays, the total changes), paid by Kono (recorded, the
        customer pays nothing more), or skip for now. Everything is kept on the quote, travels to the order and pre-fills the shipment booking there, so the carrier gets it. The customer never sees this
        panel.
      </p>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      {!view && !error && <p className="mt-2 text-sm text-gray-500">Loading the addresses and carrier options...</p>}
      {view && !view.editable && <p className="mt-2 text-sm text-red-700">This quote cannot change any more (it became an order or was replaced by a newer version); the plan is read-only.</p>}

      {view && (
        <div className="mt-3 space-y-3">
          <div className="rounded-lg border border-gray-200 bg-white p-3 text-sm">
            <div className="font-medium text-[#00062e]">Delivery: the customer and who receives</div>
            <div className="mt-1 text-xs text-gray-600">
              Quote customer: {[view.customer?.name, view.customer?.company].filter(Boolean).join(", ") || "no name on the quote"}
              {view.customer?.email ? ` · ${view.customer.email}` : ""}
              {view.customer?.phone ? ` · ${view.customer.phone}` : ""}. Below: who receives the goods at the ship-to and how to reach them (starts as the quote customer), and the delivery
              instructions for the carrier. The customer sees these at checkout and can correct them there. The pickup side (the vendor's dock) is on each line below.
            </div>
            <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
              <label className="text-xs text-gray-600">
                Receiving company
                <input className={`${INPUT} w-full`} value={receiving.company} disabled={!view.editable} onChange={(e) => setReceiving({ ...receiving, company: e.target.value })} />
              </label>
              <label className="text-xs text-gray-600">
                Person receiving
                <input className={`${INPUT} w-full`} value={receiving.name} disabled={!view.editable} onChange={(e) => setReceiving({ ...receiving, name: e.target.value })} />
              </label>
              <label className="text-xs text-gray-600">
                Phone
                <input className={`${INPUT} w-full`} value={receiving.phone} disabled={!view.editable} onChange={(e) => setReceiving({ ...receiving, phone: e.target.value })} />
              </label>
              <label className="text-xs text-gray-600">
                Email
                <input className={`${INPUT} w-full`} value={receiving.email} disabled={!view.editable} onChange={(e) => setReceiving({ ...receiving, email: e.target.value })} />
              </label>
              <label className="text-xs text-gray-600 sm:col-span-2 lg:col-span-4">
                Delivery instructions (hours, call ahead, dock, liftgate; the customer sees and may correct them at checkout)
                <textarea className={`${INPUT} w-full`} rows={2} value={deliveryInstructions} disabled={!view.editable} placeholder="e.g. deliver only between 7:00 and 14:00, call ahead, dock 3, liftgate needed" onChange={(e) => setDeliveryInstructions(e.target.value)} />
              </label>
            </div>
          </div>
          {lines.map((line) => (
            <div key={line.index} className="rounded-lg border border-gray-200 bg-white p-3">
              <div className="text-sm">
                <span className="text-xs text-gray-500">Line {line.index + 1}</span>
                <div className="font-medium text-[#00062e]">{line.summary || "Item"}</div>
                <div className="text-xs text-gray-600">Vendor: {line.supplierName || "unknown"}</div>
              </div>
              <div className="mt-2 grid gap-2 md:grid-cols-2">
                <AddressBlock
                  title="Ships from"
                  source={line.originSource === "operator" ? "corrected by an operator" : line.originSource === "vendor" ? "vendor's ship-from" : "unknown"}
                  line={line}
                  which="origin"
                  onChange={(p) => patch(line.index, (l) => ({ originDraft: { ...l.originDraft, ...p } }))}
                  onToggle={() => patch(line.index, (l) => (l.editingOrigin ? applyAddress(l, "origin") : { editingOrigin: true }))}
                />
                <AddressBlock
                  title="Delivers to"
                  source={line.destinationSource === "operator" ? "corrected by an operator" : line.destinationSource === "customer" ? "customer's ship-to" : "unknown"}
                  line={line}
                  which="destination"
                  onChange={(p) => patch(line.index, (l) => ({ destinationDraft: { ...l.destinationDraft, ...p } }))}
                  onToggle={() => patch(line.index, (l) => (l.editingDestination ? applyAddress(l, "destination") : { editingDestination: true }))}
                />
              </div>
              <PickupBlock pickup={line.pickup} editable={view.editable} onChange={(p) => patch(line.index, (l) => ({ pickup: { ...l.pickup, ...p } }))} />
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <button type="button" className={BUTTON_LIGHT} disabled={line.rating || !line.origin || !line.destination || !view.editable} onClick={() => void rate(line)}>
                  {line.rating ? "Asking WWEX..." : line.options.length ? "Get the cost again" : "Get the WWEX cost"}
                </button>
                {line.quotedAt && <span className="text-gray-500">quoted {new Date(line.quotedAt).toLocaleString()}</span>}
                {line.quoteError && <span className="text-red-700">{line.quoteError}</span>}
              </div>
              {line.options.length > 0 && (
                <div className="mt-2 space-y-1">
                  {line.options.map((o: FreightOption) => {
                    const checked = Boolean(line.selected && line.selected.vendorId === o.vendorId && line.selected.serviceCode === o.serviceCode && line.selected.amount === o.amount);
                    return (
                      <label key={`${o.vendorId}-${o.serviceCode}-${o.amount}`} className="flex cursor-pointer items-center gap-2 text-sm">
                        <input type="radio" name={`option-${line.index}`} checked={checked} disabled={!view.editable} onChange={() => patch(line.index, { selected: o })} />
                        <span className="font-medium">{money(o.amount, o.currency)}</span>
                        <span className="text-gray-700">
                          {o.carrier}
                          {o.label && o.label !== o.carrier ? ` - ${o.label}` : ""}
                        </span>
                        {o.estimatedDelivery ? <span className="text-xs text-gray-500">by {o.estimatedDelivery}</span> : null}
                      </label>
                    );
                  })}
                </div>
              )}
              <div className="mt-2 flex flex-wrap gap-3">
                {DECISIONS.map((d) => (
                  <label key={d.value} className="flex cursor-pointer items-center gap-1 text-sm" title={d.hint}>
                    <input type="radio" name={`decision-${line.index}`} checked={line.decision === d.value} disabled={!view.editable || (d.value !== "skip" && !line.selected)} onChange={() => patch(line.index, { decision: d.value })} />
                    {d.label}
                    {d.value !== "skip" && line.selected && line.decision === d.value ? <span className="text-xs text-gray-500">({money(line.selected.amount, line.selected.currency)})</span> : null}
                  </label>
                ))}
              </div>
            </div>
          ))}

          <div className="rounded-lg border border-gray-200 bg-white p-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div>
                  Customer pays for freight: <span className="font-semibold">{money(totals.customer, currency)}</span>
                  {view.quoteShipping !== null && Math.abs(view.quoteShipping - totals.customer) > 0.005 ? (
                    <span className="ml-1 text-xs text-amber-900">(the quote shows {money(view.quoteShipping, currency)} today; saving changes it)</span>
                  ) : null}
                </div>
                <div>
                  Kono pays for freight: <span className="font-semibold">{money(totals.kono, currency)}</span>
                </div>
                {view.plan.at ? (
                  <div className="text-xs text-gray-500">
                    Last saved {new Date(view.plan.at).toLocaleString()} by {view.plan.by}
                  </div>
                ) : null}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input className={`${INPUT} w-72`} value={note} placeholder="Note (optional): why, who confirmed the address..." onChange={(e) => setNote(e.target.value)} disabled={!view.editable} />
                <button type="button" className={BUTTON} disabled={saving || !view.editable} onClick={() => void save()}>
                  {saving ? "Saving..." : "Save the freight decisions"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
